import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";

/**
 * Overlay full-screen mostrato al boot del KDS finche' l'utente non ha
 * fatto un primo tap. Necessario per il browser autoplay policy: senza
 * gesture, AudioContext.resume() e Audio.play() sono bloccati.
 *
 * Feature 022 — render in App.tsx root.
 */

export default function AudioUnlockOverlay() {
  // Sempre richiedi un fresh tap al boot — l'user activation del browser
  // scade dopo qualche secondo dall'ultimo gesture, e sessionStorage non
  // riflette lo stato reale di playback abilitato. Un tap costa nulla.
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState<boolean>(false);

  useEffect(() => {
    if (unlocked) return;
    try {
      const Ctor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (Ctor) {
        const ctx = new Ctor();
        if (ctx.state === "running") {
          setUnlocked(true);
        }
        try {
          ctx.close();
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }, [unlocked]);

  if (unlocked) return null;

  const handleUnlock = async () => {
    try {
      // 1. Prime AudioContext
      const Ctor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (Ctor) {
        const ctx = new Ctor();
        await ctx.resume().catch(() => {});
      }
      // 2. Crea il singleton <audio> element keepalive. Lo play() qui (durante
      // il gesture utente) sblocca il browser autoplay policy per TUTTE le
      // future operazioni .src+.play() su questo SAME element. Pattern noto
      // come "audio keepalive" per Chrome.
      try {
        const silentMp3 =
          "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u//8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAYAAAAAAAAAAYa3jrHMAAAAAAAAAAAAAAAAAAAA";
        const w = window as any;
        if (!w.__kdsAnnouncementAudio) {
          w.__kdsAnnouncementAudio = new Audio();
          w.__kdsAnnouncementAudio.preload = "auto";
        }
        const audio = w.__kdsAnnouncementAudio as HTMLAudioElement;
        audio.src = silentMp3;
        await audio.play().catch(() => {});
      } catch {
        // ignore
      }
      // 3. Prime Web Speech
      try {
        if (window.speechSynthesis) {
          const probe = new SpeechSynthesisUtterance(" ");
          probe.volume = 0;
          window.speechSynthesis.speak(probe);
        }
      } catch {
        // ignore
      }
    } finally {
      setUnlocked(true);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleUnlock}
      onKeyDown={handleUnlock}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 text-white cursor-pointer select-none"
    >
      <div className="text-center max-w-md px-6">
        <div className="text-7xl mb-4">🔊</div>
        <div className="text-3xl font-bold mb-2">{t("kds.audio.tapToEnable", "Tocca per attivare audio")}</div>
        <div className="text-base text-white/70">
          {t(
            "kds.audio.tapReason",
            "Il display cucina ha bisogno di un primo tap per riprodurre i beep e gli annunci vocali.",
          )}
        </div>
      </div>
    </div>
  );
}
